import {
  HttpAgent,
  HttpAgentConfig,
  runHttpRequest,
  transformHttpEventStream,
} from '@ag-ui/client';
import type { RunAgentInput, RunAgentParameters } from '@ag-ui/client';
import { getPandaSearchURL, getPandaStreamURL } from '../../constants/APIconstants';

// Panda AG-UI agent build shared by web and mobile: an `@ag-ui/client` HttpAgent
// POSTs the message history to panda-service and folds the SSE event stream into
// `agent.messages`. Each host supplies its own `fetch` — auth and streaming
// support differ per platform (web: cookie; mobile: expo/fetch + Bearer).

export type FetchImpl = HttpAgentConfig['fetch'];
export type CreatePandaAgentFactoryOptions = { fetch: FetchImpl };
type CreatePandaAgentOptions = Pick<HttpAgentConfig, 'threadId' | 'initialMessages'> &
  Pick<PandaHttpAgentConfig, 'parentRunId'>;

/** HttpAgent config plus the search run this conversation continues from — the server stamps `parentRunId` on the trace so Langfuse joins the two. */
interface PandaHttpAgentConfig extends HttpAgentConfig {
  parentRunId?: string;
}

/** `/connect` answered 204: no buffered run on that instance (or past retention). */
export class NoBufferedRunError extends Error {
  constructor() {
    super('no buffered run to reconnect to');
  }
}

/**
 * HttpAgent with the AG-UI reconnect hook implemented against panda-service's
 * `POST /threads/{threadId}/connect`, which replays the active run's buffered
 * events from the start and then bridges to the live tail. Drive it via
 * `connectAgent()` — same subscriber/apply pipeline as `runAgent()`.
 */
export class PandaHttpAgent extends HttpAgent {
  readonly parentRunId?: string;

  constructor({ parentRunId, ...config }: PandaHttpAgentConfig) {
    super(config);
    this.parentRunId = parentRunId;
  }

  protected prepareRunAgentInput(parameters?: RunAgentParameters): RunAgentInput {
    const input = super.prepareRunAgentInput(parameters);
    return this.parentRunId ? { ...input, parentRunId: this.parentRunId } : input;
  }

  protected connect(input: RunAgentInput) {
    return transformHttpEventStream(
      runHttpRequest(async () => {
        const response = await this.fetch(`${this.url}/${this.threadId}/connect`, {
          method: 'POST',
          headers: {
            ...this.headers,
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
          },
          // The server ignores the body; POST is used only so AG-UI clients can send one.
          body: JSON.stringify(input),
          signal: this.abortController.signal,
        });
        if (response.status === 204) throw new NoBufferedRunError();
        return response;
      }),
      this.debugLogger,
    );
  }
}

const agentFactory =
  (resolveURL: () => string, fetchImpl: FetchImpl) =>
  ({ threadId, initialMessages, parentRunId }: CreatePandaAgentOptions = {}) =>
    new PandaHttpAgent({
      url: resolveURL(),
      fetch: fetchImpl,
      threadId,
      initialMessages,
      parentRunId,
    });

export function createPandaAgentFactory({ fetch: fetchImpl }: CreatePandaAgentFactoryOptions) {
  return agentFactory(getPandaStreamURL, fetchImpl);
}

export type CreatePandaAgent = ReturnType<typeof createPandaAgentFactory>;

// `/search` has no reconnect path; the search run only calls `runAgent`.
export function createPandaSearchAgentFactory({
  fetch: fetchImpl,
}: CreatePandaAgentFactoryOptions) {
  return agentFactory(getPandaSearchURL, fetchImpl);
}

export type CreatePandaSearchAgent = ReturnType<typeof createPandaSearchAgentFactory>;

// Stream requests bypass axios's refresh interceptor — hosts check 401 with
// this, refresh, and re-run. HttpAgent errors carry `.status`.
export function isUnauthorizedStreamError(err: unknown): boolean {
  return (err as { status?: number } | null)?.status === 401;
}

// `POST /threads` while the thread's previous run is still active server-side —
// the signal to re-attach via `connectAgent()` instead of starting a run.
export function isRunAlreadyActiveError(err: unknown): boolean {
  return (err as { status?: number } | null)?.status === 409;
}

export function isNoBufferedRunError(err: unknown): boolean {
  return err instanceof NoBufferedRunError;
}

// Rejected pre-stream with an HTTP status — mid-stream network/reader failures
// surface as plain TypeError/DOMException instead.
export function isHttpStatusError(err: unknown): boolean {
  return typeof (err as { status?: unknown } | null)?.status === 'number';
}
